// src/pages/DevTalent.js
import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Flag from 'react-country-flag'; // npm install react-country-flag
import imagesLoaded from 'imagesloaded';

const API_URL = 'https://api.securxperts.com:8000';
const ENDPOINT = '/forms/contact';

// ====================== COUNTRIES DATA ======================
const COUNTRIES = [
  { code: "+91", name: "India", iso: "IN" },
  { code: "+1",  name: "United States", iso: "US" },
  { code: "+44", name: "United Kingdom", iso: "GB" },
  { code: "+61", name: "Australia", iso: "AU" },
  { code: "+971", name: "United Arab Emirates", iso: "AE" },
  // Feel free to add more countries here
];

// ====================== COUNTRY DROPDOWN ======================
const CountryDropdown = ({ value, onChange, error, touched }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  const filteredCountries = COUNTRIES.filter(
    (c) =>
      c.code.includes(search) ||
      c.name.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selected = COUNTRIES.find((c) => c.code === value) || null;

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div
        className={`w-full p-3 rounded-xl border flex items-center gap-2 cursor-pointer transition-all ${
          error && touched
            ? "border-orange-400 bg-orange-50"
            : "border-purple-100 bg-purple-50/60 hover:border-purple-300"
        }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {selected ? (
          <>
            <Flag
              countryCode={selected.iso}
              svg
              style={{ width: '26px', height: '18px', borderRadius: '3px' }}
            />
            <span className="font-medium text-slate-800">{selected.code}</span>
          </>
        ) : (
          <span className="text-slate-400">Country Code</span>
        )}
        <span className="ml-auto text-slate-400 text-xs">▼</span>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-purple-200 rounded-xl shadow-xl max-h-64 overflow-auto">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search country or code..."
            className="w-full p-3 border-b focus:outline-none text-sm"
            autoFocus
          />
          {filteredCountries.map((country) => (
            <div
              key={country.code}
              className="p-3 hover:bg-purple-50 cursor-pointer flex items-center gap-3 text-sm"
              onClick={() => {
                onChange({ target: { name: "country_code", value: country.code } });
                setIsOpen(false);
                setSearch("");
              }}
            >
              <Flag
                countryCode={country.iso}
                svg
                style={{ width: '24px', height: '16px', borderRadius: '3px' }}
              />
              <span className="font-medium">{country.code}</span>
              <span className="text-slate-500 truncate">{country.name}</span>
            </div>
          ))}
        </div>
      )}

      {error && touched && (
        <p className="mt-1 text-xs text-orange-600 pl-1">{error}</p>
      )}
    </div>
  );
};

// ====================== SUCCESS POPUP (auto-close after 5 seconds) ======================
const SuccessPopup = ({ isOpen, onClose }) => {
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      timeoutRef.current = setTimeout(onClose, 5000);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 max-w-sm w-full shadow-2xl relative animate-fadeIn">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-500 hover:text-slate-800 text-2xl"
        >
          ×
        </button>
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-slate-800 mb-2">Success!</h3>
          <p className="text-slate-600">Your enquiry has been sent successfully.</p>
        </div>
      </div>
    </div>
  );
};

/* -------------------- IMPROVED CONTACT FORM ----------------------- */
const ContactForm = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    country_code: '',
    phoneno: '',
    location: '',
    purpose: '',
    message: '',
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isValid, setIsValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const validateField = (name, value) => {
    switch (name) {
      case "name":
        return !value.trim() ? "Name is required" : "";
      case "email":
        if (!value.trim()) return "Email is required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Invalid email";
        return "";
      case "country_code":
        return !value ? "Country code is required" : "";
      case "phoneno":
        if (!value.trim()) return "Phone number is required";
        if (value.length !== 10) return "Must be exactly 10 digits";
        return "";
      case "location":
      case "purpose":
      case "message":
        return !value.trim() ? "This field is required" : "";
      default:
        return "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    // Clean phone number
    if (name === "phoneno") {
      newValue = value.replace(/\D/g, "").slice(0, 10);
    }

    // Restrict name/location/purpose to letters + spaces
    if (["name", "location", "purpose"].includes(name)) {
      newValue = value.replace(/[^a-zA-Z\s]/g, "");
    }

    setForm((prev) => ({ ...prev, [name]: newValue }));
    setTouched((prev) => ({ ...prev, [name]: true }));

    const error = validateField(name, newValue);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  useEffect(() => {
    const requiredFields = ["name", "email", "country_code", "phoneno", "location", "purpose", "message"];
    const allFilled = requiredFields.every((field) => form[field]?.trim());
    const noErrors = Object.values(errors).every((e) => !e);
    setIsValid(allFilled && noErrors);
  }, [form, errors]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;

    setSubmitting(true);

    try {
      const resp = await fetch(`${API_URL}${ENDPOINT}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      if (resp.ok) {
        setShowSuccess(true);
        setForm({
          name: '', email: '', country_code: '', phoneno: '',
          location: '', purpose: '', message: '',
        });
        setErrors({});
        setTouched({});
      } else {
        alert("Submission failed. Please try again.");
      }
    } catch (err) {
      alert("Network error. Please check your connection.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className="max-w-3xl mx-auto bg-white shadow-lg shadow-purple-100/80 border border-purple-50 p-6 md:p-10 rounded-3xl"
        aria-label="DevTalent Enquiry Form"
      >
        <h3 className="text-2xl md:text-3xl font-bold text-center mb-8 text-slate-900">
          Start Hiring with DevTalent
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Full Name *"
              className={`w-full p-3 rounded-xl bg-purple-50/60 placeholder-slate-400 text-slate-900 outline-none border transition-all ${
                errors.name && touched.name ? "border-orange-400" : "border-purple-100 focus:border-purple-400"
              }`}
            />
            {errors.name && touched.name && (
              <p className="mt-1 text-xs text-orange-600">{errors.name}</p>
            )}
          </div>

          <div>
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Email *"
              className={`w-full p-3 rounded-xl bg-purple-50/60 placeholder-slate-400 text-slate-900 outline-none border transition-all ${
                errors.email && touched.email ? "border-orange-400" : "border-purple-100 focus:border-purple-400"
              }`}
            />
            {errors.email && touched.email && (
              <p className="mt-1 text-xs text-orange-600">{errors.email}</p>
            )}
          </div>

          <CountryDropdown
            value={form.country_code}
            onChange={handleChange}
            error={errors.country_code}
            touched={touched.country_code}
          />

          <div>
            <input
              name="phoneno"
              value={form.phoneno}
              onChange={handleChange}
              placeholder="Phone Number *"
              maxLength={10}
              className={`w-full p-3 rounded-xl bg-purple-50/60 placeholder-slate-400 text-slate-900 outline-none border transition-all ${
                errors.phoneno && touched.phoneno ? "border-orange-400" : "border-purple-100 focus:border-purple-400"
              }`}
            />
            {errors.phoneno && touched.phoneno && (
              <p className="mt-1 text-xs text-orange-600">{errors.phoneno}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <input
              name="location"
              value={form.location}
              onChange={handleChange}
              placeholder="Company Location (City, Country) *"
              className={`w-full p-3 rounded-xl bg-purple-50/60 placeholder-slate-400 text-slate-900 outline-none border transition-all ${
                errors.location && touched.location ? "border-orange-400" : "border-purple-100 focus:border-purple-400"
              }`}
            />
            {errors.location && touched.location && (
              <p className="mt-1 text-xs text-orange-600">{errors.location}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <input
              name="purpose"
              value={form.purpose}
              onChange={handleChange}
              placeholder="Purpose (Hire / Scale Team / Partnership) *"
              className={`w-full p-3 rounded-xl bg-purple-50/60 placeholder-slate-400 text-slate-900 outline-none border transition-all ${
                errors.purpose && touched.purpose ? "border-orange-400" : "border-purple-100 focus:border-purple-400"
              }`}
            />
            {errors.purpose && touched.purpose && (
              <p className="mt-1 text-xs text-orange-600">{errors.purpose}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              placeholder="Tell us about the roles, skills and team size you’re hiring for *"
              rows={4}
              className={`w-full p-3 rounded-xl bg-purple-50/60 placeholder-slate-400 text-slate-900 outline-none border transition-all resize-none ${
                errors.message && touched.message ? "border-orange-400" : "border-purple-100 focus:border-purple-400"
              }`}
            />
            {errors.message && touched.message && (
              <p className="mt-1 text-xs text-orange-600">{errors.message}</p>
            )}
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting || !isValid}
          className={`w-full mt-8 py-3 rounded-full font-semibold text-white transition-all ${
            isValid && !submitting
              ? "bg-gradient-to-r from-purple-600 to-indigo-500 hover:from-purple-700 hover:to-indigo-600"
              : "bg-gray-400 cursor-not-allowed opacity-70"
          }`}
        >
          {submitting ? 'Submitting...' : 'Start Hiring Now'}
        </button>

        <p className="text-xs mt-4 text-slate-500 text-center">
          Your details will remain confidential and used only for enquiry purposes.
        </p>
      </form>

      <SuccessPopup
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
      />
    </>
  );
};

/* -------------------- MAIN PAGE COMPONENT (unchanged content) ----------------------- */
const DevTalent = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [showScroll, setShowScroll] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  const heroVideoRef = useRef(null);

  // Image loading detection
  useEffect(() => {
    imagesLoaded(
      document.querySelectorAll('.content__item-img'),
      { background: true },
      () => {
        document.body.classList.remove('loading');
        setIsLoading(false);
      }
    );
  }, []);

  // Parallax effect (kept as is)
  useEffect(() => {
    const items = document.querySelectorAll('.content__item');
    const handleScroll = () => {
      items.forEach((item) => {
        const img = item.querySelector('.content__item-img');
        const wrapper = img?.parentNode;
        if (!img || !wrapper) return;
        const rect = item.getBoundingClientRect();
        const progress = Math.min(
          Math.max((window.innerHeight - rect.top) / (window.innerHeight + rect.height), 0),
          1
        );
        const translateY = progress * 100 - 50;
        const rotate = progress * -45;
        img.style.transform = `translateY(${translateY}px)`;
        wrapper.style.transform = `rotate3d(1, 0.3, 0.1, ${rotate}deg)`;
      });
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll progress circle
  useEffect(() => {
    const circumference = 307.919;
    const handleProgress = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? Math.min(Math.max(scrollTop / docHeight, 0), 1) : 0;
      setScrollProgress(circumference * progress);
      setShowScroll(scrollTop > 600);
    };
    window.addEventListener('scroll', handleProgress);
    handleProgress();
    return () => window.removeEventListener('scroll', handleProgress);
  }, []);

  // Video reset when scrolling to top
  useEffect(() => {
    let wasNotAtTop = false;
    const handleVideoReset = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const isAtTop = scrollTop < 50;
      if (isAtTop && wasNotAtTop && heroVideoRef.current) {
        heroVideoRef.current.currentTime = 0;
        heroVideoRef.current.play().catch(() => {});
      }
      wasNotAtTop = !isAtTop;
    };
    window.addEventListener('scroll', handleVideoReset);
    handleVideoReset();
    return () => window.removeEventListener('scroll', handleVideoReset);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (heroVideoRef.current) {
      heroVideoRef.current.currentTime = 0;
      heroVideoRef.current.play().catch(() => {});
    }
  };

  // Your existing data arrays (unchanged)
  const frameworkItems = [
    {
      title: 'Live Coding & Assessment Hub',
      desc: 'Run coding challenges and MCQs together in one seamless session, no platform switching.',
      // index: '01',
    },
    {
      title: 'Effortless Developer Recruitment',
      desc: 'Post roles and instantly receive candidates shortlisted by skills and experience.',
      // index: '02',
    },
    {
      title: 'Verified Talent Pool',
      desc: 'Access pre-assessed developers with portfolios, tech stacks and performance data.',
      // index: '03',
    },
    {
      title: 'Role-Specific Dashboards',
      desc: 'Custom dashboards let managers and recruiters view only relevant insights.',
      // index: '04',
    },
  ];

  const featureCards = [
    {
      title: 'Job & Evaluation Configuration',
      desc: 'Set up job openings and assessment workflows effortlessly within a single interface...',
    },
    {
      title: 'Intelligent Skill Assessment',
      desc: 'Create live coding challenges and MCQs with automatic scoring and instant insights...',
    },
    {
      title: 'Automated Certification & Credentialing',
      desc: 'Automatically issue verified certificates on assessment completion...',
    },
    {
      title: 'Real-Time Performance Tracking',
      desc: 'Track candidate engagement, test progression and skill outcomes via live dashboards...',
    },
    {
      title: 'Central Talent Repository',
      desc: 'Maintain portfolios, evaluation histories and skill data in a single hub...',
    },
    {
      title: 'Role-Specific Reporting & Analytics',
      desc: 'Provide customised dashboards and reports for managers, recruiters and team leads...',
    },
  ];

  return (
    <>
      <Helmet>
        <title>The Most Advanced Next-Gen Skill Evaluation Platform | DevTalent</title>
        <meta
          name="description"
          content="DevTalent helps IT companies, startups and staffing agencies hire verified developers, run live assessments, track performance, manage projects and streamline payroll from one platform."
        />
      </Helmet>

      <Navbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <video
          ref={heroVideoRef}
          className="absolute inset-0 w-full h-full object-cover opacity-20"
          autoPlay
          loop
          muted
          playsInline
        >
          <source src="/video/dev talent.mp4" type="video/mp4" />
        </video>

        <div className="absolute inset-0 bg-gradient-to-br from-[#f7f0ff] via-white to-[#f4f9ff]" />

        <div className="absolute top-0 left-5 z-20">
          <img
            src="/img/36.png"
            alt="Logo"
            className="w-20 h-auto ml-20 mt-12"
          />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 md:px-8 lg:px-10 pt-32 pb-20 md:pb-28 flex flex-col md:flex-row items-center gap-10 ml-12">
          <div className="w-full md:w-1/2 space-y-5">
            <h1 className="text-4xl font-bold text-slate-900 leading-tight mb-6">
              Premier Talent Evaluation & Assessment Platform
            </h1>
            <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
              DevTalent is an advanced talent acquisition and workforce management
              platform, designed for IT companies, startups, product teams and staffing
              agencies. It streamlines developer hiring, skill assessments, productivity
              tracking, project management and payroll within a single unified system.
            </p>
          </div>

          <div>
            <img
              src="/img/devimg1.jpg"
              alt="Hospital Dashboard"
              className="w-100 md:w-96 rounded-xl mt-12"
            />
          </div>
        </div>
      </section>

      <div className="w-full min-h-screen">
        <main className="w-full">
          <div className="content content--padded max-w-6xl mx-auto px-6 md:px-8 lg:px-10 pb-20">
            {/* Video Section */}
            <div className="max-w-7xl mx-auto">
                  <div className="max-w-3xl mx-auto mb-10 text-center">
    <h2 className="text-3xl font-bold text-[#063852]">
          Premier Talent Evaluation & Assessment Platform Demo
            </h2>
            <p className="text-gray-600 mt-3">
           DevTalent streamlines developer hiring, skill assessments, performance tracking and payroll in one platform, enabling IT teams and staffing agencies to manage talent efficiently and make data-driven decisions.
            </p>
            </div>
           
              <div className="relative rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl group">
                <video
                  className="w-full block mt-4"
                  src="/video/dev talent.mp4"
                  muted
                  playsInline
                  autoPlay
                  loop
                  preload="auto"
                  controls={false}
                  key="lauratek-video"
                  onClick={(e) => {
                    const video = e.currentTarget;
                    video.paused ? video.play() : video.pause();
                  }}
                >
                  Your browser does not support HTML5 video.
                </video>

                <div className="absolute bottom-6 right-6 md:bottom-8 md:right-8 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const video = e.currentTarget.closest('div').querySelector('video');
                      if (video.requestFullscreen) video.requestFullscreen();
                      else if (video.webkitRequestFullscreen) video.webkitRequestFullscreen();
                      else if (video.mozRequestFullScreen) video.mozRequestFullScreen();
                      else if (video.msRequestFullscreen) video.msRequestFullscreen();
                    }}
                    className="bg-black/70 hover:bg-black/90 text-white p-4 rounded-xl backdrop-blur-md transition transform hover:scale-110 shadow-2xl"
                    title="Fullscreen"
                  >
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4-4l-5-5"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Advanced Talent Management Framework */}
            <section className="mt-10 mb-10">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 mt-2">
                  Advanced Talent Management Framework
                </h2>
                <p className="text-sm md:text-base text-slate-600 mt-3 max-w-2xl mx-auto">
                  Live coding and assessment, effortless developer recruitment, verified talent pools
                  and role-specific dashboards form the core of DevTalent’s Advanced Talent
                  Management Framework.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-5">
                {frameworkItems.map((item) => (
                  <div
                    key={item.title}
                    className="bg-white rounded-2xl border border-purple-100 shadow-sm shadow-purple-50 p-5 flex flex-col space-y-3"
                  >
                    {/* <div className="w-10 h-10 rounded-xl bg-purple-50 border border-purple-100 flex items-center justify-center mb-2">
                      <img
                        src={`/img/dev${item.index}.jpg`}
                        alt={`${item.title} Icon`}
                        className="w-8 h-8 object-contain"
                      />
                    </div> */}
                    <h3 className="text-sm md:text-base font-bold text-slate-900">{item.title}</h3>
                    <p className="text-xs md:text-sm text-slate-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Streamlined Workforce Operations */}
            <section className="py-20">
              <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-28 items-center px-6">
                <div className="w-full flex justify-center">
                  <img
                    src="/img/devimg2.jpg"
                    alt="Workforce Operations"
                    className="w-full max-w-4xl rounded-2xl object-cover shadow-lg"
                  />
                </div>

                <div className="space-y-6">
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-purple-500">
                    Workforce Operations
                  </p>
                  <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900">
                    Streamlined Workforce Operations
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-sm md:text-base font-semibold text-slate-900">
                        Efficient Workflow Automation
                      </h3>
                      <p className="text-sm md:text-base text-slate-600">
                        Automated workflows cut admin overhead and simplify day-to-day
                        hiring and assessment...
                      </p>
                    </div>
                    {/* ... rest of the items ... */}
                  </div>
                </div>
              </div>
            </section>

            {/* Core Talent Management Features */}
            <section className="mb-16">
              <div className="text-center mb-8">
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-purple-500">
                  Services
                </p>
                <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 mt-2">
                  Core Talent Management Features
                </h2>
                <p className="text-sm md:text-base text-slate-600 mt-3 max-w-2xl mx-auto">
                  Automated skill assessments, instant certification, real-time analytics...
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-5">
                {featureCards.map((feature, index) => (
                  <div
                    key={feature.title}
                    className="bg-white rounded-2xl border border-purple-100 shadow-sm shadow-purple-50 p-6 flex flex-col space-y-3"
                  >
                    <div className="w-9 h-9 rounded-xl bg-purple-50 border border-purple-100 flex items-center justify-center text-[11px] font-semibold text-purple-600 mb-1">
                      {index + 1}
                    </div>
                    <h3 className="text-sm md:text-base font-bold text-slate-900">{feature.title}</h3>
                    <p className="text-xs md:text-sm text-slate-600">{feature.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact Section */}
            <section id="contact" className="mb-4">
              <div className="text-center mb-6">
                <h3 className="text-2xl md:text-3xl font-extrabold text-slate-900">
                  Tell Us About Your Hiring Needs
                </h3>
                <p className="text-sm md:text-base text-slate-600 mt-2 max-w-2xl mx-auto">
                  Whether you’re scaling a startup or building a product team, our experts
                  can help integrate DevTalent into your hiring workflow.
                </p>
              </div>

              <ContactForm />
            </section>
          </div>
        </main>
      </div>

      <Footer />

      {/* Scroll to top buttons */}
      <button
        onClick={scrollToTop}
        className={`scrollToTopBtn fixed bottom-24 right-6 z-40 transition-all duration-500 ${
          showScroll ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        <svg className="w-10 h-10 text-slate-900 drop-shadow-xl" viewBox="0 0 512 512">
          <path
            fill="currentColor"
            d="M256 108L109 296l37 37 90-90v210h48V243l90 90 37-37z"
          />
        </svg>
      </button>

      {showScroll && (
        <div
          onClick={scrollToTop}
          className="scroll-top fixed bottom-6 right-6 z-50 cursor-pointer flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 shadow-xl hover:from-purple-600 hover:to-indigo-600 transition-all"
        >
          <svg
            className="progress-circle -rotate-90 w-12 h-12 absolute"
            viewBox="-1 -1 102 102"
          >
            <path
              d="M50,1 a49,49 0 0,1 0,98 a49,49 0 0,1 0,-98"
              fill="none"
              stroke="white"
              strokeWidth="4"
              strokeDasharray="307.919"
              strokeDashoffset={307.919 - scrollProgress}
              style={{ transition: 'stroke-dashoffset 0.1s linear' }}
            />
          </svg>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-6 h-6 text-white relative z-10"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </div>
      )}
    </>
  );
};

export default DevTalent;